import { isOneDriveKnowledgeSource } from '@/features/onedrive/onedrive-knowledge.js';
import { isArtifactKnowledgeSource } from '@/features/artifacts/artifact-knowledge.js';
import type { KnowledgeSource } from '@/lib/api/workspace-knowledge.js';
import type { WorkspaceArtifact } from '@/lib/api/workspace-artifacts.js';

/**
 * Classic SharePoint "open in browser" URLs end in a viewer page, not the file's name.
 * Treating those as a label shows `Doc.aspx` instead of the real document title.
 */
function isSharePointViewerSegment(segment: string): boolean {
  const lower = segment.toLowerCase();
  return (
    lower === 'doc.aspx' ||
    lower === 'wopiframe.aspx' ||
    lower === 'allitems.aspx' ||
    lower.endsWith('.aspx')
  );
}

/**
 * Resolve a source's display name. A shelved workspace file has its name in the registry, not in the
 * source config — so the registry row wins when it is there. Otherwise: an explicit name the picker or
 * upload stored, then the name sync stamped from Graph (or from the file) after ingest, then a URL path
 * segment that is not a SharePoint viewer page (`Doc.aspx` / `WopiFrame.aspx`), then the item id.
 *
 * Shared by the knowledge-sources screen and the restrict-confirmation dialog, so a source is named
 * the same way wherever it is listed — one naming rule, not two that can drift apart.
 */
export function sourceLabel(
  source: KnowledgeSource,
  file?: WorkspaceArtifact,
): string {
  if (file) return file.name;
  const { filename, name, url, item_id: itemId } = source.config;
  if (filename) return filename;
  if (name) return name;
  if (source.indexing?.name) return source.indexing.name;
  /**
   * A personal drive's `webUrl` has no readable last segment — it ends in a form path — so a OneDrive
   * source stops at the item id rather than showing a fragment of a URL as a name.
   */
  if (url && !isOneDriveKnowledgeSource(source)) {
    try {
      const path = new URL(url).pathname.replace(/\/+$/, '');
      const segment = decodeURIComponent(
        path.split('/').filter(Boolean).pop() ?? '',
      );
      if (segment && !isSharePointViewerSegment(segment)) return segment;
    } catch {
      return url;
    }
  }
  if (isArtifactKnowledgeSource(source)) return 'Workspace file';
  return itemId ?? 'Source';
}
